import { useQuery } from '@tanstack/react-query';
import { Link, Outlet, createFileRoute, useRouterState } from '@tanstack/react-router';
import {
  Bot,
  Building2,
  FileText,
  History,
  LayoutDashboard,
  LogIn,
  Palette,
  ServerCog,
  SwatchBook,
  Tag,
} from 'lucide-react';
import { StudioToolbar } from '@/components/customisation/studio-toolbar';
import { QueryState } from '@/components/query-state';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { managedTenantsQuery } from '@/lib/customisation/queries';
import { SiteDraftProvider } from '@/lib/customisation/site-draft';
import { cn } from '@/lib/utils';

/**
 * Customisation Studio. The tenant being edited is the `tenant` search param (the default
 * tenant when absent); every child route reads it with `useStudioTenant()`. The site draft
 * store wraps every tab so branding, theme, login and dashboard edits share one document,
 * one undo history and one save/publish toolbar.
 */
export const Route = createFileRoute('/_app/admin/customisation')({
  validateSearch: (s: Record<string, unknown>): { tenant?: string } =>
    typeof s.tenant === 'string' && /^[a-z0-9-]{1,40}$/.test(s.tenant) ? { tenant: s.tenant } : {},
  component: StudioLayout,
});

interface StudioTab {
  to:
    | '/admin/customisation'
    | '/admin/customisation/branding'
    | '/admin/customisation/theme'
    | '/admin/customisation/login'
    | '/admin/customisation/dashboard'
    | '/admin/customisation/server'
    | '/admin/customisation/pages'
    | '/admin/customisation/ai'
    | '/admin/customisation/history'
    | '/admin/customisation/tenants';
  label: string;
  icon: typeof Palette;
  exact?: boolean;
  /** Only root administrators (assigned to no tenant) see it. */
  root?: boolean;
}

export const STUDIO_TABS: StudioTab[] = [
  { to: '/admin/customisation', label: 'Overview', icon: Palette, exact: true },
  { to: '/admin/customisation/branding', label: 'Branding', icon: Tag },
  { to: '/admin/customisation/theme', label: 'Theme', icon: SwatchBook },
  { to: '/admin/customisation/login', label: 'Login page', icon: LogIn },
  { to: '/admin/customisation/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { to: '/admin/customisation/server', label: 'Server pages', icon: ServerCog },
  { to: '/admin/customisation/pages', label: 'Pages', icon: FileText },
  { to: '/admin/customisation/ai', label: 'AI builder', icon: Bot },
  { to: '/admin/customisation/history', label: 'History', icon: History },
  { to: '/admin/customisation/tenants', label: 'Tenants', icon: Building2, root: true },
];

/** The tenant slug the studio is editing. */
export function useStudioTenant(): string {
  const { tenant } = Route.useSearch();
  const tenants = useQuery(managedTenantsQuery);
  if (tenant) return tenant;
  return (
    tenants.data?.tenants.find((t) => t.isDefault)?.slug ??
    tenants.data?.tenants[0]?.slug ??
    'default'
  );
}

function StudioLayout() {
  const tenants = useQuery(managedTenantsQuery);
  const search = Route.useSearch();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const tenant = useStudioTenant();
  const trimmed = pathname.replace(/\/+$/, '') || '/';
  return (
    <QueryState query={tenants}>
      {(data) => (
        <SiteDraftProvider key={tenant} tenant={tenant}>
          <div className="-mx-4 -mt-6 flex min-h-[calc(100dvh-3.5rem)] flex-col sm:-mx-6 sm:-mt-8 lg:-mx-8">
            <StudioToolbar
              tenantSwitcher={
                data.tenants.length > 1 && (
                  <Select
                    value={tenant}
                    onValueChange={(v) => {
                      window.location.assign(`${trimmed}?tenant=${encodeURIComponent(v)}`);
                    }}
                  >
                    <SelectTrigger className="h-8 w-44" aria-label="Tenant">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {data.tenants.map((t) => (
                        <SelectItem key={t.id} value={t.slug}>
                          {t.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )
              }
            />
            <nav aria-label="Studio sections" className="bg-card/60 border-b px-4 sm:px-6 lg:px-8">
              <ul className="-mb-px flex gap-1 overflow-x-auto [scrollbar-width:none]">
                {STUDIO_TABS.filter((tab) => !tab.root || data.root).map((tab) => {
                  const active = tab.exact ? trimmed === tab.to : trimmed.startsWith(tab.to);
                  return (
                    <li key={tab.to}>
                      <Link
                        to={tab.to}
                        search={search}
                        aria-current={active ? 'page' : undefined}
                        className={cn(
                          'flex h-10 items-center gap-2 border-b-2 px-3 text-sm font-medium whitespace-nowrap transition-colors',
                          'focus-visible:ring-ring/50 outline-none focus-visible:ring-[3px]',
                          active
                            ? 'border-brand text-foreground'
                            : 'text-muted-foreground hover:text-foreground border-transparent',
                        )}
                      >
                        <tab.icon className="size-4" aria-hidden />
                        {tab.label}
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </nav>
            <div className="min-w-0 flex-1">
              <Outlet />
            </div>
          </div>
        </SiteDraftProvider>
      )}
    </QueryState>
  );
}
